/**
 * Hook to safely use Convex features with PostHog feature flag protection
 * Provides fallback behavior when Convex features are disabled
 * Updated to use explicit fetching instead of WebSocket subscriptions
 */

import { useState, useEffect, useCallback } from "react";
import { useMutation } from "convex/react";
import { useAuth } from "@clerk/clerk-react";
import { api } from "../convex/_generated/api";
import { featureFlags } from "../services/featureFlags";
import { convex } from "../lib/convex";

export interface ConvexFeatures {
  // User management
  currentUser: any | null;
  isUserLoading: boolean;
  refetchUser: () => Promise<void>;

  // Jobs management
  userJobs: any[] | null | undefined;
  isJobsLoading: boolean;
  refetchJobs: () => Promise<void>;

  // Feature availability
  isConvexEnabled: boolean;

  // Actions
  updateUserPreferences: (preferences: any) => Promise<void>;
  createJob: (jobData: any) => Promise<string>;
}

/**
 * Main hook for Convex features with feature flag protection
 */
export const useConvexFeatures = (): ConvexFeatures => {
  const { isSignedIn, userId } = useAuth();
  const isConvexEnabled = featureFlags.isConvexEnabled();

  // State for user profile (explicit fetching)
  const [currentUser, setCurrentUser] = useState<any | null>(null);
  const [isUserLoading, setIsUserLoading] = useState(true);

  // State for user jobs (explicit fetching)
  const [userJobs, setUserJobs] = useState<any[] | null>(null);
  const [isJobsLoading, setIsJobsLoading] = useState(true);

  // Explicit fetch for current user
  const refetchUser = useCallback(async () => {
    if (!isConvexEnabled || !isSignedIn) {
      setIsUserLoading(false);
      return;
    }

    try {
      setIsUserLoading(true);
      const userData = await convex.query(api.users.getCurrentUser, {});
      setCurrentUser(userData);
      console.log('👤 User profile fetched:', userData);
    } catch (error) {
      console.error('❌ Error fetching user profile:', error);
    } finally {
      setIsUserLoading(false);
    }
  }, [isConvexEnabled, isSignedIn]);

  // Explicit fetch for user jobs
  const refetchJobs = useCallback(async () => {
    if (!isConvexEnabled || !isSignedIn || !userId || !featureFlags.isEnabled('convex-job-queries')) {
      setIsJobsLoading(false);
      return;
    }

    try {
      setIsJobsLoading(true);
      // No clerkId needed - automatically uses authenticated user from ctx.auth
      const jobs = await convex.query(api.jobs.getUserJobHistory, {});
      setUserJobs(jobs);
      console.log('📋 User jobs fetched:', jobs);
    } catch (error) {
      console.error('❌ Error fetching user jobs:', error);
    } finally {
      setIsJobsLoading(false);
    }
  }, [isConvexEnabled, isSignedIn, userId]);

  // Fetch on mount
  useEffect(() => {
    refetchUser();
  }, [refetchUser]);

  useEffect(() => {
    refetchJobs();
  }, [refetchJobs]);

  const updateUserPreferencesRaw = useMutation(api.users.updatePreferences);
  const createJobRaw = useMutation(api.jobs.createJob);

  // Wrapped actions with Convex integration and fallback behavior
  const updateUserPreferences = async (preferences: any): Promise<void> => {
    if (isConvexEnabled && isSignedIn && featureFlags.isEnabled('convex-preferences-sync')) {
      try {
        await updateUserPreferencesRaw({ preferences });
      } catch (error) {
        console.error('Failed to update user preferences in Convex:', error);
        // Fallback to localStorage
        localStorage.setItem('sortingcat_user_preferences', JSON.stringify(preferences));
      }
    } else {
      // Fallback: Saving preferences to localStorage
      localStorage.setItem('sortingcat_user_preferences', JSON.stringify(preferences));
    }
  };

  const createJob = async (jobData: any): Promise<string> => {
    if (isConvexEnabled && isSignedIn && featureFlags.isEnabled('convex-job-queries')) {
      try {
        return await createJobRaw(jobData);
      } catch (error) {
        console.error('Failed to create job in Convex:', error);
        // Fallback to localStorage
        const jobId = `local_${Date.now()}`;
        const localJobs = JSON.parse(localStorage.getItem('sortingcat_jobs') || '[]');
        localJobs.push({ ...jobData, id: jobId, createdAt: Date.now() });
        localStorage.setItem('sortingcat_jobs', JSON.stringify(localJobs));
        return jobId;
      }
    } else {
      // Fallback: Creating mock job in localStorage
      const jobId = `local_${Date.now()}`;
      const localJobs = JSON.parse(localStorage.getItem('sortingcat_jobs') || '[]');
      localJobs.push({ ...jobData, id: jobId, createdAt: Date.now() });
      localStorage.setItem('sortingcat_jobs', JSON.stringify(localJobs));
      return jobId;
    }
  };

  return {
    currentUser,
    isUserLoading,
    refetchUser,
    userJobs,
    isJobsLoading,
    refetchJobs,
    isConvexEnabled,
    updateUserPreferences,
    createJob,
  };
};

/**
 * Hook to check specific Convex feature availability
 */
export const useConvexFeature = (feature: string): boolean => {
  return featureFlags.isConvexEnabled() && featureFlags.isEnabled(feature as any);
};

/**
 * Hook for development/debugging - shows current feature flag state
 */
export const useConvexDebug = () => {
  if (!featureFlags.isDebugEnabled()) {
    return { debug: false };
  }

  return {
    debug: true,
    flags: featureFlags.getAllFlags(),
    convexEnabled: featureFlags.isConvexEnabled(),
  };
};